import { Link } from 'react-router-dom';
import { ChefHat, Clock } from 'lucide-react';
import { useRecipes } from '../hooks/useRecipes';
import { useSiteContent } from '../hooks/useSiteContent';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { minutesLabel } from '../types/recipe';
import { Reveal } from '../components/Reveal';

/** Elenco delle ricette pubblicate (/ricette). Titolo e introduzione da Contenuti → Ricette. */
export default function Recipes() {
  const { recipes, loading } = useRecipes();
  const { content: page, loaded } = useSiteContent('page.recipes');
  // Un admin riceve anche le bozze dalla RLS: qui si mostra solo il pubblicato.
  const published = recipes.filter((r) => r.published);

  useDocumentMeta(page.title, page.intro);

  return (
    <div>
      <section className="bg-brand">
        <div className={`container py-16 md:py-20 ${loaded ? '' : 'invisible'}`}>
          <span className="mb-6 block h-1 w-12 rounded-full bg-[#FFB95F]" />
          <h1 className="font-display text-4xl font-bold leading-tight text-white text-balance md:text-5xl">{page.title}</h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">{page.intro}</p>
        </div>
      </section>

      <section className="container py-14">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-primary" />
          </div>
        ) : published.length === 0 ? (
          <div className="mx-auto max-w-md py-16 text-center">
            <ChefHat className="mx-auto mb-4 h-10 w-10 text-primary" />
            <h2 className="mb-2 font-display text-2xl font-semibold text-foreground">Le prime ricette arrivano presto</h2>
            <p className="text-muted-foreground">Stiamo raccogliendo le nostre preferite da condividere con te.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {published.map((recipe, i) => {
              const total = (recipe.prep_minutes ?? 0) + (recipe.cook_minutes ?? 0);
              return (
                <Reveal key={recipe.id} index={i} className="h-full">
                  <Link
                    to={`/ricette/${recipe.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-lg"
                  >
                    <div className="h-52 overflow-hidden bg-muted">
                      {recipe.image_url ? (
                        <img
                          src={recipe.image_url}
                          alt={recipe.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-primary/60">
                          <ChefHat className="h-12 w-12" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <h2 className="mb-2 font-display text-xl font-semibold text-foreground">{recipe.title}</h2>
                      <p className="mb-4 line-clamp-3 flex-1 text-sm text-muted-foreground">{recipe.summary}</p>
                      {total > 0 && (
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" />
                          {minutesLabel(total)}
                        </span>
                      )}
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
